/**
 * Centralized API Configuration
 * 
 * Single source of truth for all API endpoints and keys used throughout the application.
 * 
 * ## Best Practice: Use with React Query
 * 
 * This app uses React Query for data fetching. The recommended pattern is to use
 * raw fetch with explicit constants from this file within React Query hooks:
 * 
 * @example
 * // ✅ RECOMMENDED: Raw fetch with React Query (explicit, transparent)
 * import { MAIN_API_URL, MAIN_API_KEY } from '../config/api';
 * 
 * const { data } = useQuery({
 *   queryKey: ['users'],
 *   queryFn: async () => {
 *     const response = await fetch(`${MAIN_API_URL}/users`, {
 *       headers: {
 *         'Content-Type': 'application/json',
 *         'X-API-Key': MAIN_API_KEY
 *       }
 *     });
 *     if (!response.ok) throw new Error('Failed to fetch');
 *     return response.json();
 *   }
 * });
 * 
 * @example
 * // 🔧 OPTIONAL: Service instances for edge cases (outside React Query)
 * import { mainApi } from '../services/api';
 * const data = await mainApi.get('/users');
 * 
 * @module config/api
 */

export const API_CONFIG = {
    // Main Backend API
    MAIN_API_URL: import.meta.env.VITE_MAIN_API_URL || 'http://localhost:8000',
    MAIN_API_KEY: import.meta.env.VITE_MAIN_API_KEY || '',

    // Indexer Service API
    INDEXER_API_URL: import.meta.env.VITE_INDEXER_API_URL || 'http://localhost:8080',
    INDEXER_API_KEY: import.meta.env.VITE_INDEXER_API_KEY || 'test-indexer-key-12345',

    // GCP Project ID (for Cloud Console links)
    GCP_PROJECT_ID: import.meta.env.VITE_GCP_PROJECT_ID || '',
    // Region for Cloud Console deep links. Was hardcoded 'us-central1' at three call
    // sites, which broke every link once the stack moved region.
    GCP_REGION: import.meta.env.VITE_GCP_REGION || 'us-east1',
} as const;

/**
 * Main backend API URL
 * @constant {string}
 */
export const MAIN_API_URL = API_CONFIG.MAIN_API_URL;

/**
 * Main backend API key
 * @constant {string}
 */
export const MAIN_API_KEY = API_CONFIG.MAIN_API_KEY;

/**
 * Indexer service API URL
 * @constant {string}
 */
export const INDEXER_API_URL = API_CONFIG.INDEXER_API_URL;

/**
 * Indexer service API key
 * @constant {string}
 */
export const INDEXER_API_KEY = API_CONFIG.INDEXER_API_KEY;

/**
 * GCP Project ID (for Cloud Console links)
 * @constant {string}
 */
export const GCP_PROJECT_ID = API_CONFIG.GCP_PROJECT_ID;
export const GCP_REGION = API_CONFIG.GCP_REGION;

/**
 * Helper function to get standard headers for main API requests.
 * 
 * **Optional utility** - can be used to reduce boilerplate in fetch calls.
 * 
 * @returns {Object} Headers object with Content-Type and X-API-Key
 * 
 * @example
 * // Instead of writing:
 * const response = await fetch(`${MAIN_API_URL}/users`, {
 *   headers: {
 *     'Content-Type': 'application/json',
 *     'X-API-Key': MAIN_API_KEY
 *   }
 * });
 * 
 * // You can use:
 * const response = await fetch(`${MAIN_API_URL}/users`, {
 *   headers: getMainApiHeaders()
 * });
 */
export const getMainApiHeaders = () => ({
    'Content-Type': 'application/json',
    'X-API-Key': MAIN_API_KEY,
});

/**
 * Helper function to get standard headers for indexer API requests.
 * 
 * **Optional utility** - can be used to reduce boilerplate in fetch calls.
 * 
 * @returns {Object} Headers object with Content-Type and X-API-Key
 * 
 * @example
 * // Instead of writing:
 * const response = await fetch(`${INDEXER_API_URL}/jobs`, {
 *   headers: {
 *     'Content-Type': 'application/json',
 *     'X-API-Key': INDEXER_API_KEY
 *   }
 * });
 * 
 * // You can use:
 * const response = await fetch(`${INDEXER_API_URL}/jobs`, {
 *   headers: getIndexerApiHeaders()
 * });
 */
export const getIndexerApiHeaders = () => ({
    'Content-Type': 'application/json',
    'X-API-Key': INDEXER_API_KEY,
});

/**
 * Typed fetch wrapper for main API calls.
 *
 * Handles headers, error parsing (FastAPI `detail` field), and response JSON
 * parsing in one place. Use inside React Query `queryFn` / `mutationFn`.
 *
 * @example
 * const data = await fetchMainApi<User[]>("/bo/users");
 * const result = await fetchMainApi<Job>("/api/backfill/jobs", {
 *   method: "POST",
 *   body: JSON.stringify(payload),
 * });
 */
export async function fetchMainApi<T>(
    endpoint: string,
    init: RequestInit = {},
): Promise<T> {
    const response = await fetch(`${MAIN_API_URL}${endpoint}`, {
        ...init,
        headers: {
            ...getMainApiHeaders(),
            ...init.headers,
        },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        const detail = errorData.detail;
        const message = Array.isArray(detail)
            ? detail.map((d: { msg?: string }) => d.msg || String(d)).join("; ")
            : detail || errorData.message || `Request failed (${response.status})`;
        throw new Error(message);
    }

    return response.json();
}

